import { useState } from 'react';
import { Divider, Drawer } from 'antd';
import { MdInfoOutline } from 'react-icons/md';
import { StyledButton } from '@app/components/button/Button';
import { StyledTooltip } from '@app/components/tooltip/Tooltip';

const FlowBuilderGuide = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    return (
        <>
            <StyledTooltip title="View Flow Builder Guide">
                <StyledButton
                    iconOnly
                    icon={<MdInfoOutline size={20} />}
                    color="blue"
                    onClick={() => setIsDrawerOpen(true)}
                    data-testid="flow-builder-guide-btn"
                    className="flow-builder-guide-btn"
                    aria-label="Open Flow Builder Guide"
                />
            </StyledTooltip>
            <Drawer
                title="Flow Builder Guide"
                open={isDrawerOpen}
                closable={{ 'aria-label': 'Close Button' }}
                onClose={() => setIsDrawerOpen(false)}
                size={800}>
                <div className="flow-builder-guide">
                    <div className="section">
                        <h5>Using the Flow Builder</h5>
                        <div>
                            <div>
                                Create new nodes by dragging and dropping into the Flow Builder.
                            </div>
                            <div>
                                Connect nodes by dragging a line from the connection point of one
                                node to the connection point of another.
                            </div>
                            <div>Double click a node to open the content editing drawer.</div>
                            <div>
                                Single click to select nodes and connections, these will be
                                highlighted <span className="bordered-span red">red</span>. Press
                                "Return" to delete.
                            </div>
                            <div>
                                Select multiple nodes and/or connections by drawing a box with
                                "Shift" and "click" held.
                            </div>
                            <div>
                                Existing connections can be moved to connection points by dragging
                                from the existing connection point when highlighted{' '}
                                <span className="bordered-span orange">orange</span> (if required,
                                bring connections to the top by first selecting).
                            </div>
                            <div>
                                Drag the Flow Builder background (or minimap) to navigate around the
                                flow. Scroll, or double click, to zoom in and out.
                            </div>
                        </div>
                    </div>
                    <Divider />
                    <div className="section">
                        <h6>Flow Structure</h6>
                        <div>
                            <div>
                                There are 4 node types -{' '}
                                <span className="bordered-span green highlighted-text">Action</span>
                                ,{' '}
                                <span className="bordered-span orange highlighted-text">
                                    Decision
                                </span>
                                ,{' '}
                                <span className="bordered-span purple highlighted-text">
                                    Section
                                </span>
                                , and{' '}
                                <span className="bordered-span blue highlighted-text">
                                    Question
                                </span>
                                .
                            </div>
                            <div>
                                <span className="underlined-span blue highlighted-text">
                                    Questions
                                </span>{' '}
                                can only be nested within{' '}
                                <span className="underlined-span purple highlighted-text">
                                    Sections
                                </span>
                                {''}.
                            </div>
                            <div>
                                Subsections can be created from{' '}
                                <span className="underlined-span green highlighted-text">
                                    Actions
                                </span>{' '}
                                and{' '}
                                <span className="underlined-span purple highlighted-text">
                                    Sections
                                </span>
                                {''}
                                {''}.
                            </div>
                            <div>
                                <span className="underlined-span orange highlighted-text">
                                    Decisions
                                </span>{' '}
                                allow an action to be take depending on whether assessment of a
                                value passes or fails.
                            </div>
                            <div>
                                There is no limit to the length of a flow but they cannot be
                                circular.
                            </div>
                        </div>
                    </div>
                    <div className="section">
                        <h6>Visibility Conditions</h6>
                        <div>
                            <div>
                                By default, all nodes are visible unconditionally but can have up to
                                three visibility conditions.
                                <ul>
                                    <li>
                                        Values connected to the same point represent an "AND"
                                        condition group (i.e. all conditions in the group must be
                                        met for the node to be visible).
                                    </li>
                                    <li>
                                        Values connected to different points represent "OR"
                                        condition groups, thereby allowing visibility to be toggled
                                        via different conditions.
                                    </li>
                                </ul>
                            </div>
                            <div>
                                <span className="underlined-span blue highlighted-text">
                                    Questions
                                </span>{' '}
                                can be provided with two or more accepted answers. These values can
                                then be used to determine visibility of other nodes.
                            </div>
                        </div>
                    </div>
                    <div className="section">
                        <h6>Context Variables</h6>
                        <div>
                            <div>
                                Context variables are placeholders that signify where dynamic
                                information can be inserted into a flow. They must be written as{' '}
                                <code>{'{{ variable_name }}'}</code>
                                {''}.
                            </div>
                            <div>
                                Default values for any unused variables will be lost when a flow is
                                saved.
                            </div>
                        </div>
                    </div>
                    <div className="section">
                        <h6>Saving/Exporting</h6>
                        <div>
                            <div>
                                Flows cannot be saved if the following errors exist.
                                <ul>
                                    <li>The flow does not begin with a Section.</li>
                                    <li>There is more than one flow.</li>
                                    <li>There are incomplete or unconnected nodes present.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </Drawer>
        </>
    );
};

export default FlowBuilderGuide;
